import { useRef, useState } from 'react'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { ActionButton, Checkbox, Hint, SectionTitle, StaleBanner, SummaryBar, ToolbarRow } from './styles'
import { DemandRulesEditor } from './DemandRulesEditor'
import { ConductorFields } from './ConductorFields'
import { getFeederColumnCount, getFeederLoadedCount } from './conductorConfig'
import {
  Container,
  Card,
  InputDiv,
  Select,
  PageContainer,
  PageHeader,
  InfoCard,
  Badge,
  BackButton,
  ActionButtonsRow,
} from '../../../styles/global'
import { FaPlusCircle } from 'react-icons/fa'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { BoardsResult } from './BoardsResult'
import { BoardTabs } from './BoardTabs'
import { CircuitModal } from './CircuitModal'
import { CircuitsTable, getCircuitPower } from './CircuitsTable'
import { IdrModal } from './IdrModal'
import { IdrsTable } from './IdrsTable'
import { useBoardsState } from './useBoardsState'
import { useCalculation } from './useCalculation'

const SYSTEMS = ['127V/220V', '220V/380V']
const SUPPLY_TYPES = [
  { value: 'single-phase', label: 'Monofásica (fase e neutro)' },
  { value: 'two-phase', label: 'Bifásica (duas fases e neutro)' },
  { value: 'three-phase', label: 'Trifásica (três fases e neutro)' },
]
const HEADER_GRADIENT = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'

export function ElectricalCircuits() {
  const state = useBoardsState()
  const resultRef = useRef(null)
  const fileInputRef = useRef(null)
  // circuitModal: { index } (index null = novo circuito); idrModal: { idr } (idr null = novo IDR).
  const [circuitModal, setCircuitModal] = useState(null)
  const [idrModal, setIdrModal] = useState(null)

  const { canCalculate, handleCalculate, invalid } = useCalculation({
    state,
    onCalculated: () =>
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150),
  })

  const { activeBoard, circuits, activeIdrs, demandRules, supplyType, result } = state
  const totalCircuits = state.boards.reduce((total, board) => total + board.circuits.length, 0)
  const installedPower = circuits.reduce((total, circuit) => total + getCircuitPower(circuit), 0)

  return (
    <PageContainer>
      <PageHeader $gradient={HEADER_GRADIENT}>
        <div className="header-content">
          <BackButton to="/eletrica">
            <FiArrowLeft /> Voltar para Elétrica
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/eletrica">Elétrica</a>
            <span>/</span>
            <span>Circuitos</span>
          </div>
          <h1>Dimensionamento de circuitos</h1>
          <p className="subtitle">
            Calcule condutores e proteções considerando tensão, fator de demanda, potência instalada e tipo de carga
            conforme NBR 5410.
          </p>
          <div className="badges">
            <Badge>NBR 5410</Badge>
            <Badge>Fatores de demanda</Badge>
            <Badge>Proteções</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
        $border="2px solid #fb923c"
        $primaryColor="#c2410c"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Organize seus circuitos de iluminação e tomadas, defina fatores de agrupamento e obtenha correntes,
              disjuntores e cabos recomendados.
            </p>
            <p>
              Ajuste os fatores de temperatura e demanda para refletir o cenário real do projeto antes de exportar os
              resultados.
            </p>
            <div className="norms">
              <span>NBR 5410</span>
              <span>Instalações de baixa tensão</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Circuitos</h2>
          <h3>Dados gerais</h3>
          <InputDiv>
            <span>Sistema</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                id="systemSelect"
                value={state.selectedSystem}
                onChange={(event) => state.changeSystem(event.target.value)}
              >
                {SYSTEMS.map((system) => (
                  <option key={system} value={system}>
                    {system}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
          <InputDiv>
            <span>Alimentação</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                id="supplyTypeSelect"
                value={supplyType}
                onChange={(event) => state.changeSupplyType(event.target.value)}
              >
                {SUPPLY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
          <InputDiv>
            <span>
              Fator de temperatura (F<sub>t</sub>)
            </span>
            <input
              type="number"
              value={state.temperatureFactor}
              onChange={(event) => state.changeTemperatureFactor(event.target.value)}
            />
          </InputDiv>
          <DemandRulesEditor
            rules={demandRules}
            onSave={state.saveRule}
            onDelete={state.deleteRule}
            onRestoreDefaults={state.restoreDefaultRules}
            isLoading={state.isLoadingRules}
          />

          <SectionTitle>Quadros</SectionTitle>
          <BoardTabs
            boards={state.boards}
            activeBoardId={activeBoard.id}
            onSelect={state.selectBoard}
            onAdd={state.addBoard}
            onRemove={state.removeBoard}
          />
          <SummaryBar aria-label={`Resumo de ${activeBoard.name}`}>
            <strong>{activeBoard.name}</strong>
            <span>
              Circuitos: <b>{circuits.length}</b>
            </span>
            <span>
              Potência instalada: <b>{installedPower} VA</b>
            </span>
            <span>
              IDRs: <b>{activeIdrs.length}</b>
            </span>
          </SummaryBar>
          <ConductorFields
            key={`board-${activeBoard.id}`}
            idPrefix="board"
            title={`Cabo de alimentação - ${activeBoard.name}`}
            config={activeBoard}
            onChange={(config) => state.changeBoardConfig(activeBoard.id, config)}
            conductorOptions={state.conductorOptions}
            groupingOptions={state.groupingOptions}
            columnCount={getFeederColumnCount(state.conductorOptions, supplyType)}
            loadedCount={getFeederLoadedCount(state.conductorOptions, supplyType, activeBoard.neutralLoaded)}
          >
            {supplyType === 'three-phase' && (
              <>
                <InputDiv>
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      state.changeBoardConfig(activeBoard.id, {
                        ...activeBoard,
                        neutralLoaded: !activeBoard.neutralLoaded,
                      })
                    }
                  >
                    Considerar o neutro como condutor carregado
                  </span>
                  <div>
                    <Checkbox
                      id="neutralLoaded"
                      checked={Boolean(activeBoard.neutralLoaded)}
                      onChange={(event) =>
                        state.changeBoardConfig(activeBoard.id, { ...activeBoard, neutralLoaded: event.target.checked })
                      }
                    />
                  </div>
                </InputDiv>
                <Hint>
                  Condição prevista em 6.2.5.6.1 da NBR 5410 (por exemplo, harmônicas): usa 4 condutores carregados e o
                  fator 0,86. A API não detecta essa condição, então indique-a apenas quando ela ocorrer.
                </Hint>
              </>
            )}
          </ConductorFields>

          <ToolbarRow>
            <h3>Circuitos - {activeBoard.name}</h3>
            <ActionButton type="button" onClick={() => setCircuitModal({ index: null })}>
              <FaPlusCircle style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Novo circuito
            </ActionButton>
          </ToolbarRow>
          <Hint>Arraste as linhas para reordenar. A potência é a soma dos elementos de cada circuito.</Hint>
          <CircuitsTable
            circuits={circuits}
            idrs={activeIdrs}
            demandRules={demandRules}
            invalidIds={invalid[activeBoard.id] || new Set()}
            onEdit={(index) => setCircuitModal({ index })}
            onDuplicate={state.duplicateCircuit}
            onRemove={state.removeCircuit}
            onMove={state.moveCircuit}
          />

          <ToolbarRow>
            <h3>IDRs - {activeBoard.name}</h3>
            <ActionButton type="button" onClick={() => setIdrModal({ idr: null })}>
              <FaPlusCircle style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Novo IDR
            </ActionButton>
          </ToolbarRow>
          <Hint>
            O escopo (geral, grupo ou individual) é deduzido dos vínculos. Para um IDR geral, vincule todos os circuitos
            do quadro; circuitos sem IDR são permitidos.
          </Hint>
          <IdrsTable
            idrs={activeIdrs}
            circuits={circuits}
            onEdit={(idr) => setIdrModal({ idr })}
            onRemove={state.removeIdr}
          />

          <ActionButtonsRow $isFormValid={totalCircuits >= 1}>
            <button type="button" onClick={state.saveToFile}>
              Salvar
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()}>
              Carregar
            </button>
            <button type="button" disabled={totalCircuits < 1 || !canCalculate} onClick={handleCalculate}>
              {!canCalculate ? 'Limite atingido' : 'Calcular'}
            </button>
          </ActionButtonsRow>
          <input type="file" ref={fileInputRef} onChange={state.loadFromFile} style={{ display: 'none' }} />
        </Card>

        <div ref={resultRef} style={{ scrollMarginTop: '90px' }}>
          {result !== '' && (
            <>
              {state.isStale && (
                <StaleBanner role="status">
                  Resultado desatualizado - recalcule para refletir as alterações.
                </StaleBanner>
              )}
              <BoardsResult result={result} demandRules={demandRules} />
            </>
          )}
        </div>
      </Container>

      {circuitModal && (
        <CircuitModal
          circuit={circuitModal.index === null ? null : circuits[circuitModal.index]}
          boardName={activeBoard.name}
          demandRules={demandRules}
          idrs={activeIdrs}
          allowsTwoPoles={state.allowsTwoPoles}
          conductorOptions={state.conductorOptions}
          groupingOptions={state.groupingOptions}
          onClose={() => setCircuitModal(null)}
          onSave={(data) => {
            state.saveCircuit(circuitModal.index, data)
            setCircuitModal(null)
          }}
        />
      )}
      {idrModal && (
        <IdrModal
          idr={idrModal.idr}
          boardName={activeBoard.name}
          circuits={circuits}
          onClose={() => setIdrModal(null)}
          onSave={(data) => {
            state.saveIdr(idrModal.idr?.id ?? null, data)
            setIdrModal(null)
          }}
        />
      )}
    </PageContainer>
  )
}
